import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import {
    Video, Bone, Activity, Users, Trophy, FileText, Search, Brain,
    Play, ArrowRight, CheckCircle, Sparkles, BarChart3, Heart,
    Zap, Eye, Target, Award, ChevronRight, Upload, Download
} from 'lucide-react';

const PlatformDemoPage = () => {
    const [activeStep, setActiveStep] = useState(0);

    const journey = [
        {
            id: 'upload',
            icon: Upload,
            color: 'cyan',
            title: '1. Upload Your Performance Video',
            short: 'Drop a 10-30 sec clip from training or competition',
            features: [
                'Chunked upload supports videos up to 200 MB',
                'Categorize by drill type (e.g. squash forehand, basketball free-throw)',
                'Multi-video drill portfolio per athlete'
            ],
            cta: { label: 'Try Video Upload', path: '/video-analysis' }
        },
        {
            id: 'ai',
            icon: Brain,
            color: 'purple',
            title: '2. AI Analyses Every Frame',
            short: 'Gemini Vision + MediaPipe + YOLOv8 working in concert',
            features: [
                'Gemini Vision identifies key moments & highlights',
                'MediaPipe BlazePose tracks 33 body keypoints across all frames',
                'YOLOv8 + ByteTrack handles multi-player position tracking',
                'Skeleton overlay shows joint angles, posture, and movement'
            ],
            cta: { label: 'See Live Demo', path: '/demo/biomechanics' }
        },
        {
            id: 'reports',
            icon: FileText,
            color: 'green',
            title: '3. Get a Pro Performance Report',
            short: 'Radar charts, heatmaps, injury risk flags, exportable PDF',
            features: [
                'Sport-specific metrics (sprint speed, reaction time, accuracy)',
                'Injury risk panel — knee valgus, ankle drift, asymmetry',
                'Movement heatmap showing court coverage',
                'Branded PDF report ready to share with coaches/scouts'
            ],
            cta: { label: 'View Sample Report', path: '/demo/sample-report' }
        },
        {
            id: 'rank',
            icon: Trophy,
            color: 'yellow',
            title: '4. Climb the Combined Ranking',
            short: 'Hybrid ranking that fuses tournaments + AI + video',
            features: [
                'Normal Ranking — Tournament wins, ELO, points',
                'AI Talent Score — Potential, growth rate, consistency',
                'Video Analysis — Latest performance score',
                'Adjustable weights for scout/federation/athlete preferences'
            ],
            cta: { label: 'See Combined Rankings', path: '/leaderboard' }
        },
        {
            id: 'discover',
            icon: Search,
            color: 'pink',
            title: '5. Get Discovered by Scouts',
            short: 'Your profile is searchable in the Scouting Portal',
            features: [
                'Public athlete profile with QR code',
                'Scouts shortlist you, compare you side-by-side',
                'Federation talent pipeline tracks your progression',
                'Direct contact through verified scouts'
            ],
            cta: { label: 'Open Scouting Portal', path: '/scouting' }
        }
    ];

    const features = [
        { icon: Bone, title: 'Skeleton Overlay', desc: '33-point body tracking with joint angles', color: 'cyan' },
        { icon: Activity, title: 'Heatmap Analytics', desc: 'Movement & territory coverage', color: 'red' },
        { icon: BarChart3, title: 'Radar Charts', desc: 'Sport-specific performance metrics', color: 'purple' },
        { icon: Heart, title: 'Injury Risk', desc: 'Biomechanical red flag detection', color: 'orange' },
        { icon: Eye, title: 'Player Tracking', desc: 'YOLOv8 + ByteTrack for team analysis', color: 'green' },
        { icon: Zap, title: 'Live Match Intel', desc: 'Real-time stats via WebSocket', color: 'yellow' },
    ];

    return (
        <div className="min-h-screen bg-[#0a1628] text-white">
            {/* Hero */}
            <div className="relative overflow-hidden border-b border-white/10">
                <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/10 via-purple-500/10 to-pink-500/10" />
                <div className="relative max-w-6xl mx-auto px-6 py-16 md:py-24">
                    <div className="inline-flex items-center gap-2 px-3 py-1 bg-purple-500/20 border border-purple-500/40 rounded-full text-xs font-medium text-purple-300 mb-6">
                        <Sparkles className="w-3 h-3" /> Platform Walkthrough
                    </div>
                    <h1 className="text-4xl md:text-6xl font-bold mb-4 leading-tight">
                        How <span className="text-cyan-400">NextGen Vision</span> works
                    </h1>
                    <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mb-8">
                        From a single training clip to a scouted, ranked, and recruited athlete — see the full journey our AI takes for you.
                    </p>
                    <div className="flex gap-3 flex-wrap">
                        <Link to="/demo/biomechicsa">
                            <Button className="bg-purple-500 hover:bg-purple-600 text-white" data-testid="hero-watch-demo">
                                <Play className="w-4 h-4 mr-2" /> Watch Live Demo
                            </Button>
                        </Link>
                        <Link to="/video-analysis">
                            <Button variant="outline" className="border-white/20" data-testid="hero-upload-cta">
                                <Upload className="w-4 h-4 mr-2" /> Upload Your Video
                            </Button>
                        </Link>
                    </div>
                </div>
            </div>

            {/* Journey */}
            <div className="max-w-6xl mx-auto px-6 py-12">
                <h2 className="text-2xl md:text-3xl font-bold mb-2">The Athlete Journey</h2>
                <p className="text-muted-foreground mb-10">Five steps from upload to recruitment</p>

                <div className="grid grid-cols-1 lg:grid-cols-5 gap-3 mb-10">
                    {journey.map((step, i) => (
                        <button
                            key={step.id}
                            onClick={() => setActiveStep(i)}
                            className={`p-4 text-left rounded-lg border transition-all ${activeStep === i ? `bg-${step.color}-500/20 border-${step.color}-500` : 'bg-[#0d2847] border-white/10 hover:border-white/30'}`}
                            data-testid={`journey-step-${step.id}`}
                        >
                            <step.icon className={`w-6 h-6 mb-2 text-${step.color}-400`} />
                            <div className="text-xs font-bold mb-1">{step.title}</div>
                            <div className="text-xs text-muted-foreground line-clamp-2">{step.short}</div>
                        </button>
                    ))}
                </div>

                {/* Active Step Detail */}
                <Card className="bg-[#0d2847] border-white/10 p-8" data-testid="journey-detail">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
                        <div>
                            <div className={`inline-flex p-3 rounded-lg bg-${journey[activeStep].color}-500/20 mb-4`}>
                                {React.createElement(journey[activeStep].icon, { className: `w-8 h-8 text-${journey[activeStep].color}-400` })}
                            </div>
                            <h3 className="text-2xl font-bold mb-3">{journey[activeStep].title}</h3>
                            <p className="text-muted-foreground mb-6">{journey[activeStep].short}</p>
                            <ul className="space-y-2 mb-6">
                                {journey[activeStep].features.map((f, i) => (
                                    <li key={i} className="flex items-start gap-2 text-sm">
                                        <CheckCircle className={`w-4 h-4 mt-0.5 text-${journey[activeStep].color}-400 flex-shrink-0`} />
                                        <span>{f}</span>
                                    </li>
                                ))}
                            </ul>
                            <Link to={journey[activeStep].cta.path}>
                                <Button className={`bg-${journey[activeStep].color}-500 hover:bg-${journey[activeStep].color}-600 text-white`}>
                                    {journey[activeStep].cta.label} <ArrowRight className="w-4 h-4 ml-2" />
                                </Button>
                            </Link>
                        </div>
                        <div className={`aspect-video bg-gradient-to-br from-${journey[activeStep].color}-500/20 to-${journey[activeStep].color}-500/5 rounded-lg flex items-center justify-center border border-white/10`}>
                            {/* Animated mock visualization */}
                            <DemoVisualization step={journey[activeStep].id} color={journey[activeStep].color} />
                        </div>
                    </div>
                </Card>
            </div>

            {/* Features Grid */}
            <div className="max-w-6xl mx-auto px-6 py-12 border-t border-white/10">
                <h2 className="text-2xl md:text-3xl font-bold mb-2">Core Capabilities</h2>
                <p className="text-muted-foreground mb-10">The AI engines powering every analysis</p>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {features.map((f, i) => (
                        <Card key={i} className="bg-[#0d2847] border-white/10 p-5 hover:border-white/30 transition-colors" data-testid={`feature-${f.title.toLowerCase().replace(' ', '-')}`}>
                            <f.icon className={`w-7 h-7 text-${f.color}-400 mb-3`} />
                            <h4 className="font-semibold mb-1">{f.title}</h4>
                            <p className="text-sm text-muted-foreground">{f.desc}</p>
                        </Card>
                    ))}
                </div>
            </div>

            {/* Sample Report Preview */}
            <div className="max-w-6xl mx-auto px-6 py-12 border-t border-white/10">
                <div className="flex items-center justify-between mb-8 flex-wrap gap-4">
                    <div>
                        <h2 className="text-2xl md:text-3xl font-bold mb-2">What does a Performance Report look like?</h2>
                        <p className="text-muted-foreground">Every analysis produces a downloadable PDF + interactive web view</p>
                    </div>
                    <Link to="/demo/sample-report">
                        <Button variant="outline" className="border-white/20" data-testid="view-sample-report-btn">
                            <FileText className="w-4 h-4 mr-2" /> View Sample
                        </Button>
                    </Link>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <SampleReportCard
                        title="Skeleton Overlay"
                        description="33-keypoint body tracking on every frame"
                        bg="from-cyan-500/30 to-cyan-900/30"
                    >
                        <SkeletonPreview />
                    </SampleReportCard>
                    <SampleReportCard
                        title="Movement Heatmap"
                        description="Court coverage & territory dominance"
                        bg="from-red-500/30 to-orange-900/30"
                    >
                        <HeatmapPreview />
                    </SampleReportCard>
                    <SampleReportCard
                        title="Performance Radar"
                        description="Sport-specific multi-axis metrics"
                        bg="from-purple-500/30 to-purple-900/30"
                    >
                        <RadarPreview />
                    </SampleReportCard>
                </div>
            </div>

            {/* CTA */}
            <div className="max-w-6xl mx-auto px-6 py-16">
                <Card className="bg-gradient-to-r from-cyan-500/20 via-purple-500/20 to-pink-500/20 border-purple-500/40 p-8 md:p-12 text-center">
                    <Trophy className="w-12 h-12 text-yellow-400 mx-auto mb-4" />
                    <h2 className="text-3xl md:text-4xl font-bold mb-3">Ready to see yourself ranked?</h2>
                    <p className="text-muted-foreground max-w-xl mx-auto mb-6">
                        Upload your first video and join the talent pipeline today. Free for athletes.
                    </p>
                    <div className="flex gap-3 justify-center flex-wrap">
                        <Link to="/video-analysis"><Button className="bg-purple-500 hover:bg-purple-600 text-white" data-testid="cta-upload-video">Upload Video</Button></Link>
                        <Link to="/leaderboard"><Button variant="outline" className="border-white/20" data-testid="cta-view-rankings">See Rankings</Button></Link>
                    </div>
                </Card>
            </div>
        </div>
    );
};

const DemoVisualization = ({ step, color }) => {
    if (step === 'upload') {
        return (
            <div className="text-center">
                <Upload className={`w-20 h-20 text-${color}-400 mx-auto mb-3 animate-pulse`} />
                <div className="text-xs text-muted-foreground">Drop video → chunks upload → ready</div>
            </div>
        );
    }
    if (step === 'ai') return <SkeletonPreview large />;
    if (step === 'reports') return <RadarPreview large />;
    if (step === 'rank') return (
        <div className="space-y-2 w-full px-8">
            {[
                { label: 'Normal', val: 78, c: 'yellow' },
                { label: 'AI Talent', val: 92, c: 'purple' },
                { label: 'Video', val: 85, c: 'cyan' },
                { label: 'Combined', val: 84, c: 'green' }
            ].map((b, i) => (
                <div key={i}>
                    <div className="flex justify-between text-xs mb-1"><span>{b.label}</span><span className="font-bold">{b.val}</span></div>
                    <div className="h-2 bg-white/10 rounded">
                        <div className={`h-full bg-${b.c}-400 rounded transition-all`} style={{ width: `${b.val}%` }} />
                    </div>
                </div>
            ))}
        </div>
    );
    if (step === 'discover') return (
        <div className="grid grid-cols-3 gap-2 p-6">
            {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="aspect-square bg-white/5 rounded-lg flex items-center justify-center border border-white/10 hover:border-pink-500 transition-colors">
                    <Users className="w-6 h-6 text-pink-400" />
                </div>
            ))}
        </div>
    );
    return <Sparkles className={`w-20 h-20 text-${color}-400`} />;
};

const SkeletonPreview = ({ large = false }) => {
    const size = large ? 200 : 120;
    return (
        <svg viewBox="0 0 100 100" className={`${large ? 'w-48 h-48' : 'w-32 h-32'}`}>
            {/* Skeleton body */}
            <line x1="50" y1="20" x2="50" y2="55" stroke="#22c55e" strokeWidth="2" />
            <line x1="50" y1="30" x2="35" y2="50" stroke="#22c55e" strokeWidth="2" />
            <line x1="50" y1="30" x2="65" y2="50" stroke="#22c55e" strokeWidth="2" />
            <line x1="35" y1="50" x2="30" y2="70" stroke="#22c55e" strokeWidth="2" />
            <line x1="65" y1="50" x2="70" y2="70" stroke="#22c55e" strokeWidth="2" />
            <line x1="50" y1="55" x2="40" y2="85" stroke="#22c55e" strokeWidth="2" />
            <line x1="50" y1="55" x2="60" y2="85" stroke="#22c55e" strokeWidth="2" />
            {/* Joints */}
            {[[50, 15], [50, 30], [35, 50], [65, 50], [30, 70], [70, 70], [50, 55], [40, 85], [60, 85]].map(([x, y], i) => (
                <circle key={i} cx={x} cy={y} r="3" fill="#3b82f6" stroke="white" strokeWidth="1">
                    <animate attributeName="r" values="3;4;3" dur="2s" repeatCount="indefinite" begin={`${i * 0.1}s`} />
                </circle>
            ))}
        </svg>
    );
};

const HeatmapPreview = () => (
    <svg viewBox="0 0 100 60" className="w-40 h-24">
        <rect x="0" y="0" width="100" height="60" fill="#0a1628" />
        <rect x="40" y="0" width="2" height="60" fill="#22c55e" opacity="0.3" />
        {/* Heatmap blobs */}
        {[
            { cx: 30, cy: 35, r: 12, op: 0.7 },
            { cx: 60, cy: 25, r: 10, op: 0.5 },
            { cx: 75, cy: 40, r: 8, op: 0.4 },
            { cx: 20, cy: 20, r: 6, op: 0.3 },
        ].map((b, i) => (
            <circle key={i} cx={b.cx} cy={b.cy} r={b.r} fill="url(#heat)" opacity={b.op}>
                <animate attributeName="r" values={`${b.r};${b.r + 2};${b.r}`} dur="3s" repeatCount="indefinite" begin={`${i * 0.4}s`} />
            </circle>
        ))}
        <defs>
            <radialGradient id="heat">
                <stop offset="0%" stopColor="#ef4444" />
                <stop offset="60%" stopColor="#f59e0b" stopOpacity="0.6" />
                <stop offset="100%" stopColor="#fbbf24" stopOpacity="0" />
            </radialGradient>
        </defs>
    </svg>
);

const RadarPreview = ({ large = false }) => {
    const points = [
        { label: 'Speed', val: 85 },
        { label: 'Accuracy', val: 78 },
        { label: 'Power', val: 92 },
        { label: 'Stamina', val: 70 },
        { label: 'Reaction', val: 88 },
    ];
    const cx = 50, cy = 50, r = 35;
    const coords = points.map((p, i) => {
        const angle = (i / points.length) * 2 * Math.PI - Math.PI / 2;
        const dist = (p.val / 100) * r;
        return [cx + dist * Math.cos(angle), cy + dist * Math.sin(angle)];
    });
    const polyPath = coords.map(c => c.join(',')).join(' ');
    return (
        <svg viewBox="0 0 100 100" className={large ? 'w-48 h-48' : 'w-36 h-36'}>
            {[0.25, 0.5, 0.75, 1].map(s => (
                <polygon key={s} points={points.map((p, i) => {
                    const angle = (i / points.length) * 2 * Math.PI - Math.PI / 2;
                    return [cx + r * s * Math.cos(angle), cy + r * s * Math.sin(angle)].join(',');
                }).join(' ')} fill="none" stroke="#a855f7" strokeOpacity="0.2" strokeWidth="0.5" />
            ))}
            <polygon points={polyPath} fill="#a855f7" fillOpacity="0.4" stroke="#a855f7" strokeWidth="1.5" />
        </svg>
    );
};

const SampleReportCard = ({ title, description, bg, children }) => (
    <Card className={`bg-gradient-to-br ${bg} border-white/10 p-6 overflow-hidden`}>
        <div className="aspect-square flex items-center justify-center mb-4">{children}</div>
        <h4 className="font-semibold mb-1">{title}</h4>
        <p className="text-xs text-muted-foreground">{description}</p>
    </Card>
);

export default PlatformDemoPage;
